import { useEffect, useId, useMemo, useState } from 'react';
import { createColumnHelper, tableFeatures, useTable } from '@tanstack/react-table';
import { Trash2 } from 'lucide-react';
import type { OrcamentoExibicao, OrcamentoItemExibicao, OrcamentoItemMc } from '@triade/contracts';
import { SeloFaixaMc } from '@/components/margem-contribuicao';
import { ProfitBadge } from '@/components/profit-badge';
import { registrarEdicao, removerEdicao } from '@/lib/edicao-pendente';
import { formatarDecimal, formatarDinheiro, formatarFracaoPercentual, formatarMoeda, formatarTextoDigitado } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { LinhaForm } from './tipos';

const features = tableFeatures({});
const col = createColumnHelper<typeof features, LinhaForm>();

export interface ItensGridProps {
  linhas: LinhaForm[];
  /** Totais/itens da API para estas linhas; undefined enquanto calcula (as celulas calculadas mostram "…"). */
  exibicao: OrcamentoExibicao | undefined;
  /** false = o resultado em `exibicao` ainda nao corresponde ao que esta digitado. */
  atual: boolean;
  editavel: boolean;
  onAlterar: (chave: string, campo: 'quantidade' | 'preco', valor: string) => void;
  onRemover: (chave: string) => void;
  /** MC dos itens gravados por chave da linha. Ausente na simulacao (a API so manda MC do orcamento gravado). */
  mcPorChave?: ReadonlyMap<string, OrcamentoItemMc>;
  /** Tem lucro.ver. Sem ele a API omite MC e lucro numerico: a coluna MC some e o lucro vira so a cor da faixa. Padrao: sim. */
  comLucro?: boolean;
}

const INPUT =
  'tabular h-8 w-[92px] rounded-lg border border-input bg-card px-2 text-right font-mono outline-none focus-visible:border-primary focus-visible:shadow-focus disabled:opacity-60';

/** Move o foco como planilha: Enter/seta para baixo = mesma coluna na linha de baixo; seta para cima = acima. */
function navegar(e: React.KeyboardEvent<HTMLInputElement>, coluna: string, indice: number) {
  const passo = e.key === 'Enter' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
  if (passo === 0) return;
  const alvo = document.querySelector<HTMLInputElement>(`[data-grid="${coluna}-${indice + passo}"]`);
  if (alvo) {
    e.preventDefault();
    alvo.focus();
    alvo.select();
  }
}

/**
 * Input com estado local: digitar so mexe no texto da celula; o valor sobe (e a API recalcula) no blur, Enter ou Tab.
 * Fica FORA do FlexRender de proposito: as colunas sao recriadas a cada resposta do calculo e o FlexRender trata a
 * funcao `cell` como componente novo, remontando o input (perda de foco a cada digito).
 */
function CelulaNumerica({
  campo,
  linha,
  indice,
  editavel,
  onConfirmar,
}: {
  campo: 'quantidade' | 'preco';
  linha: LinhaForm;
  indice: number;
  editavel: boolean;
  onConfirmar: (chave: string, campo: 'quantidade' | 'preco', valor: string) => void;
}) {
  const externo = linha[campo];
  const [texto, setTexto] = useState(externo);
  const [editando, setEditando] = useState(false);
  const id = useId();
  useEffect(() => {
    if (!editando) setTexto(externo);
  }, [externo, editando]);

  const confirmar = () => {
    if (texto !== externo) onConfirmar(linha.chave, campo, texto);
  };

  // Texto digitado e ainda nao confirmado: Salvar (botao ou ⌘S) o confirma antes de gravar, e a barra ja conta como alterado.
  const pendente = editando && texto !== externo;
  useEffect(() => {
    if (!pendente) return;
    registrarEdicao(id, () => onConfirmar(linha.chave, campo, texto));
    return () => removerEdicao(id);
  }, [pendente, id, onConfirmar, linha.chave, campo, texto]);

  return (
    <input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      aria-label={`${campo === 'quantidade' ? 'Quantidade' : 'Negociado'} item ${linha.codigo}`}
      data-grid={`${campo}-${indice}`}
      // Fora de edicao o preco aparece nas casas da empresa; o texto guardado (e enviado) e o digitado, sem arredondar.
      value={editando || campo !== 'preco' ? texto : formatarTextoDigitado(texto, 'dinheiro')}
      disabled={!editavel}
      onFocus={() => setEditando(true)}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => {
        confirmar();
        setEditando(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp') confirmar();
        navegar(e, campo, indice);
      }}
      className={INPUT}
    />
  );
}

/** Grade densa dos itens (TanStack Table). Digitar so altera o texto; os numeros mostrados vem da API (`exibicao`). */
export function ItensGrid({ linhas, exibicao, atual, editavel, onAlterar, onRemover, mcPorChave, comLucro = true }: ItensGridProps) {
  const temMc = comLucro && (mcPorChave?.size ?? 0) > 0;
  const temReforma = exibicao?.itens.some((i) => i.nfLinha !== undefined) ?? false;
  const calculado = (indice: number): OrcamentoItemExibicao | undefined =>
    exibicao && exibicao.itens.length === linhas.length ? exibicao.itens[indice] : undefined;
  const pendente = (v: React.ReactNode) => (atual ? v : <span className="text-ink-disabled">…</span>);

  const columns = useMemo(
    () => [
      col.display({
        id: 'produto',
        header: 'Produto',
        footer: () => `${linhas.length} ${linhas.length === 1 ? 'item' : 'itens'}`,
        cell: ({ row }) => {
          const l = row.original;
          const c = calculado(row.index);
          const prejuizo = mcPorChave?.get(l.chave)?.prejuizoMarginal === true;
          return (
            <div>
              <div className="flex flex-wrap items-center gap-2 font-medium">
                {l.descricao}
                {prejuizo && (
                  <span data-testid="selo-prejuizo-marginal" className="rounded-full bg-danger-bg px-2 py-0.5 text-caption font-semibold text-danger-ink">
                    Prejuízo marginal
                  </span>
                )}
              </div>
              <div className="text-caption text-ink-muted">
                <span className="tabular">{l.codigo}</span> · {l.unidade}
                {c?.costSource ? ` · custo por ${ORIGEM[c.costSource] ?? c.costSource}` : ''}
              </div>
            </div>
          );
        },
      }),
      col.display({
        id: 'quantidade',
        header: 'Qtd.',
        footer: () => (exibicao ? <span className="tabular font-semibold">{formatarDecimal(exibicao.quantidadeTotal, 0)}</span> : null),
        cell: () => null, // renderizada em CelulaNumerica (fora do FlexRender, para o input nao remontar)
      }),
      col.display({
        id: 'preco',
        header: 'Negociado',
        cell: () => null, // renderizada em CelulaNumerica (fora do FlexRender, para o input nao remontar)
      }),
      col.display({
        id: 'ipi',
        header: 'IPI',
        cell: ({ row }) => {
          const c = calculado(row.index);
          return pendente(c ? <span className="text-ink-secondary">{formatarFracaoPercentual(c.ipiRate)}</span> : '—');
        },
      }),
      col.display({
        id: 'total',
        header: 'Total',
        footer: () => (exibicao ? <span className="tabular font-bold">{formatarDinheiro(exibicao.receitaItens)}</span> : null),
        cell: ({ row }) => {
          const c = calculado(row.index);
          return pendente(c ? <b className="font-semibold">{formatarDinheiro(c.total)}</b> : '—');
        },
      }),
      col.display({
        id: 'lucro',
        header: 'Lucro',
        footer: () =>
          exibicao && exibicao.faixa ? (
            <ProfitBadge lucro={(exibicao.lucro ?? exibicao.lucroExibido) ?? undefined} faixa={exibicao.faixa} meta={exibicao.metaLucro} minimo={exibicao.minimoLucro ?? '0'} compacto className="font-bold" />
          ) : null,
        cell: ({ row }) => {
          const c = calculado(row.index);
          return pendente(
            c && exibicao ? (
              <ProfitBadge lucro={c.lucro ?? undefined} faixa={c.faixa} meta={exibicao.metaLucro} minimo={exibicao.minimoLucro ?? '0'} compacto />
            ) : (
              '—'
            ),
          );
        },
      }),
      ...(temReforma
        ? [
            col.display({
              id: 'reforma',
              header: 'CBS/IBS · NF',
              cell: ({ row }) => {
                const c = calculado(row.index);
                if (!c || c.nfLinha === undefined) return <span className="text-ink-disabled">—</span>;
                return (
                  <span className="inline-flex flex-col items-end gap-0.5 text-caption text-ink-muted">
                    <span>+ {formatarMoeda(c.cbsIbsValor)}</span>
                    <span>NF {formatarMoeda(c.nfLinha)}</span>
                  </span>
                );
              },
            }),
          ]
        : []),
      ...(temMc
        ? [
            col.display({
              id: 'mc',
              header: 'MC%',
              cell: ({ row }) => {
                const mc = mcPorChave?.get(row.original.chave);
                if (!mc) return null;
                return (
                  <span className="inline-flex flex-col items-end gap-0.5">
                    {mc.pct === null ? <span className="text-ink-disabled">—</span> : <SeloFaixaMc faixa={mc.faixa} rotulo={formatarFracaoPercentual(mc.pct)} />}
                    {mc.valor !== undefined && <span className="text-caption text-ink-muted">{formatarMoeda(mc.valor)}</span>}
                  </span>
                );
              },
            }),
          ]
        : []),
      col.display({
        id: 'remover',
        header: () => <span className="sr-only">Remover</span>,
        cell: ({ row }) =>
          editavel ? (
            <button
              type="button"
              aria-label={`Remover item ${row.original.codigo}`}
              onClick={() => onRemover(row.original.chave)}
              className="inline-flex size-8 items-center justify-center rounded-lg text-ink-muted hover:bg-line-soft hover:text-danger-ink"
            >
              <Trash2 aria-hidden="true" className="size-4" />
            </button>
          ) : null,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [linhas, exibicao, atual, editavel, onAlterar, onRemover, mcPorChave, temMc, temReforma],
  );

  const table = useTable({ features, columns, data: linhas, getRowId: (l) => l.chave });
  const direita = new Set(['quantidade', 'preco', 'ipi', 'total', 'lucro', 'mc', 'reforma', 'remover']);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] border-collapse text-small">
        <thead>
          {table.getHeaderGroups().map((g) => (
            <tr key={g.id} className="text-left text-caption text-ink-muted">
              {g.headers.map((h) => (
                <th key={h.id} scope="col" className={cn('px-3 py-2.5 font-medium first:px-5 last:px-5', direita.has(h.column.id) && 'text-right')}>
                  <table.FlexRender header={h} />
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              data-testid="linha-item"
              data-prejuizo-marginal={mcPorChave?.get(row.original.chave)?.prejuizoMarginal ? 'true' : undefined}
              className={cn('border-t border-line-soft align-middle', mcPorChave?.get(row.original.chave)?.prejuizoMarginal && 'border-l-[3px] border-l-destructive bg-danger-bg')}
            >
              {row.getAllCells().map((cell) => (
                <td key={cell.id} className={cn('tabular px-3 py-2.5 first:px-5 last:px-5', direita.has(cell.column.id) ? 'text-right' : 'font-sans')}>
                  {cell.column.id === 'quantidade' || cell.column.id === 'preco' ? (
                    <CelulaNumerica
                      campo={cell.column.id}
                      linha={row.original}
                      indice={row.index}
                      editavel={editavel}
                      onConfirmar={onAlterar}
                    />
                  ) : (
                    <table.FlexRender cell={cell} />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          {table.getFooterGroups().map((g) => (
            <tr key={g.id} className="border-t border-border bg-surface-muted">
              {g.headers.map((h) => (
                <td key={h.id} className={cn('tabular px-3 py-3 first:px-5 last:px-5', direita.has(h.column.id) ? 'text-right' : 'font-sans font-semibold')}>
                  {h.isPlaceholder ? null : <table.FlexRender footer={h} />}
                </td>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>
    </div>
  );
}

const ORIGEM: Record<string, string> = { ficha: 'ficha', aquisicao: 'aquisição', composicao: 'composição' };
